import { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import store, { AppDispatch } from '@/store/store';
import { setOpenedInspectionGps } from '@/store/slices/openInspectionSlice';
import { RootState } from '@/store/rootReducer';
import { logDebug, logError } from '@/utils/logger';
import { systemConfig } from '@/config/systemConfig';
import { getCurrentPosition } from '@/utils/geolocation';

const GPS_RETRY_MS = 20_000;
const GPS_TIMEOUT_MS = 10_000;
const GPS_MAX_AGE_MS = 60_000;
const MIN_ACCURACY_METERS = 100; // Reject poor accuracy

export function useInspectionGpsCapture(inspectionId: string | undefined) {
    const dispatch = useDispatch<AppDispatch>();
    const inspection = useSelector((state: RootState) => state.openedInspection.inspection);

    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isMounted = useRef(true);
    const retryCountRef = useRef(0);

    const debugLog = (message: string, data: any) => {
        console.log('debugLog', message, data);
        logDebug(systemConfig.apiUrl, store.getState().auth.token || "", store.getState().auth.user, message, { ...data, version: systemConfig.version });
    }

    const errorLog = (message: string, data: any) => {
        console.log('errorLog', message, data);
        logError(systemConfig.apiUrl, store.getState().auth.token || "", store.getState().auth.user, message, { ...data, version: systemConfig.version });
    }

    // Helper to clear existing timeouts
    const clearGpsTimeout = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    };

    const tryGetPosition = useCallback(() => {
        if (!isMounted.current || !inspectionId) return;

        const options: PositionOptions = {
            enableHighAccuracy: true,
            timeout: GPS_TIMEOUT_MS,
            maximumAge: GPS_MAX_AGE_MS,
        };

        getCurrentPosition(options)
            .then((position) => {
                if (!isMounted.current) return;
                const { latitude, longitude, accuracy } = position.coords;

                if (accuracy > MIN_ACCURACY_METERS) {
                    retryCountRef.current += 1;
                    debugLog(
                        `GPS: retry #${retryCountRef.current} - accuracy (${accuracy}m) too low (threshold=${MIN_ACCURACY_METERS}m), scheduling another attempt...`
                        , { inspectionId, accuracy });
                    clearGpsTimeout();
                    timeoutRef.current = setTimeout(tryGetPosition, GPS_RETRY_MS);
                    return;
                }

                debugLog(
                    `GPS: position acquired (lat=${latitude}, lon=${longitude}, accuracy=${accuracy}m) after ${retryCountRef.current} retries.`
                    , { inspectionId, latitude, longitude, accuracy });
                retryCountRef.current = 0;
                dispatch(setOpenedInspectionGps({ id: inspectionId, latitude, longitude }));
                clearGpsTimeout();
            })
            .catch((error: GeolocationPositionError) => {
                if (!isMounted.current) return;

                if (error.code === error.PERMISSION_DENIED) {
                    errorLog('GPS: permission denied by user – stopping further attempts.', { inspectionId });
                    return;
                }

                // For TIMEOUT or POSITION_UNAVAILABLE, retry
                retryCountRef.current += 1;
                errorLog(
                    `GPS: retry #${retryCountRef.current} scheduled due to error (code=${error.code}, message="${error.message}").`,
                    { inspectionId, error }
                );
                clearGpsTimeout();
                timeoutRef.current = setTimeout(tryGetPosition, GPS_RETRY_MS);
            });
    }, [dispatch, inspectionId]);
    useEffect(() => {
        isMounted.current = true;
        // Initial Check: Do we already have valid coords?
        const hasCoordinates =
            inspection?.latitude != null &&
            inspection?.longitude != null &&
            inspection.latitude !== 0;

        const canCapture =
            inspectionId &&
            inspection?.id === inspectionId &&
            typeof navigator !== 'undefined' &&
            'geolocation' in navigator;

        if (canCapture && !hasCoordinates) {
            retryCountRef.current = 0;
            debugLog(`GPS: starting capture for inspection "${inspectionId}".`, { inspectionId });
            tryGetPosition();
        }

        return () => {
            isMounted.current = false;
            clearGpsTimeout();
        };
    }, [inspectionId, inspection?.id, inspection?.latitude, tryGetPosition]);
}